import { SeoLandingLayout, type SeoPageContent } from '@/components/seo/SeoLandingLayout'

const content: SeoPageContent = {
  locale: 'de',
  path: '/familienbudget-app',
  alternatePath: '/family-budget-app',
  seo: {
    title: 'Familienbudget-App — Haushaltskosten gemeinsam planen | Equna',
    description:
      'Equna ist die Familienbudget-App für Eltern: gemeinsame Ausgaben erfassen, Budgets pro Bereich planen, Belege speichern und monatlich abschließen. Made in Germany.',
  },
  hero: {
    eyebrow: 'Für Familien',
    h1: 'Familienbudget-App — Überblick statt Überraschungen',
    sub: 'Lebensmittel, Wohnen, Kinder, Freizeit: Equna macht aus chaotischen Einzelausgaben ein Familienbudget mit klaren Bereichen, Warnungen und Monatsabschluss.',
    note: 'Kostenlos starten. DSGVO-konform. Made in Germany.',
  },
  problems: {
    heading: 'Warum Familienbudgets so schwer zu halten sind',
    intro:
      'Familien haben viele kleine Ausgaben in vielen Bereichen — und es kommen ständig neue dazu. Ohne System sieht man erst auf der Kontoabrechnung, wo das Geld geblieben ist.',
    items: [
      {
        title: 'Viele kleine Ausgaben',
        description:
          'Drogerie, Schulbedarf, Mitbringsel, Geburtstage — einzeln klein, in Summe entscheidend.',
      },
      {
        title: 'Keine Echtzeit-Sicht',
        description:
          'Erst am Monatsende sieht man, dass das Lebensmittelbudget zerflossen ist. Da ist es zu spät.',
      },
      {
        title: 'Unterschiedliche Verteilungen',
        description:
          'Manche Bereiche werden 50/50 geteilt, andere nach Einkommen — manuell nicht zu pflegen.',
      },
      {
        title: 'Keine gemeinsame Wahrheit',
        description:
          'Jeder Elternteil hat eine eigene Vorstellung vom Stand. Aus „gefühlt zu viel" wird Streit.',
      },
    ],
  },
  solutions: {
    heading: 'So schafft Equna Überblick im Familienbudget',
    intro:
      'Drei oder mehr Budget-Bereiche, klare Verteilung und ein Monatsabschluss, den beide Eltern bestätigen.',
    items: [
      {
        icon: 'home',
        title: 'Budgets pro Bereich',
        description:
          'Lebensmittel, Wohnen, Freizeit, Kinder — definiert Budgets dort, wo sie wehtun, und seht sofort, wo ihr steht.',
      },
      {
        icon: 'split',
        title: 'Globale Verteilung',
        description:
          'Eine Regel für alle gemeinsamen Ausgaben. Optional pro Ausgabe abweichen, wenn nötig.',
      },
      {
        icon: 'kids',
        title: 'Auch Kinder-Ausgaben',
        description:
          'Schulbedarf, Hobbys, Arzt — pro Kind erfasst und sauber ausgewertet.',
      },
      {
        icon: 'receipt',
        title: 'Belege direkt mitspeichern',
        description:
          'Kassenzettel oder Online-Rechnung als Foto an der Ausgabe. Endlich kein Schuhkarton mehr.',
      },
      {
        icon: 'calendar',
        title: 'Monatsabschluss',
        description:
          'Beide Eltern bestätigen den Monat. Equna rechnet den Ausgleich und archiviert den Stand.',
      },
      {
        icon: 'shield',
        title: 'DSGVO-konform',
        description:
          'Daten auf deutschen Servern. Aktuell ohne Werbung, kein Tracking, kein Verkauf an Dritte.',
      },
    ],
  },
  comparison: {
    heading: 'Vorher vs. mit Equna',
    columns: ['Ohne Familienbudget-App', 'Mit Equna'],
    rows: [
      {
        topic: 'Überblick',
        without: 'Erst am Monatsende auf dem Kontoauszug',
        with: 'Stand jederzeit im Dashboard',
      },
      {
        topic: 'Budgets',
        without: 'Im Kopf — wird vergessen',
        with: 'Pro Bereich definiert, mit Warnung',
      },
      {
        topic: 'Verteilung',
        without: 'Diskussion pro Ausgabe',
        with: 'Einmal festlegen, gilt für alles',
      },
      {
        topic: 'Belege',
        without: 'Im Schuhkarton oder Mail-Ordner',
        with: 'Direkt an der Ausgabe',
      },
      {
        topic: 'Monatsschnitt',
        without: 'Findet meistens nicht statt',
        with: 'Beide bestätigen, archiviert',
      },
    ],
  },
  trust: {
    heading: 'Warum Familien Equna vertrauen',
    items: [
      {
        title: 'Made in Germany',
        description: 'Entwickelt und betrieben in Berlin. Server bei IONOS in Deutschland.',
      },
      {
        title: 'DSGVO-konform',
        description: 'Ausgaben und Belege bleiben bei euch. Keine Weitergabe an Dritte zu Werbezwecken.',
      },
      {
        title: 'Beide Eltern gleichberechtigt',
        description: 'Keine Hauptperson. Beide Konten haben dieselben Rechte und dieselbe Sicht.',
      },
      {
        title: 'Kostenlos starten',
        description: 'Die Grundversion deckt alles ab, was Familien im Alltag brauchen.',
      },
    ],
  },
  relatedBlog: {
    heading: 'Mehr zum Thema lesen',
    posts: [
      {
        title: 'Haushaltskosten fair teilen — 5 Modelle',
        description: 'Welches Verteilungsmodell passt zu eurer Familie? Vier Beispiele plus Empfehlung.',
        href: '/blog/haushaltskosten-fair-teilen-fuenf-modelle',
      },
      {
        title: '3-Konten-Modell und Equna',
        description: 'Warum ein gemeinsames Familienkonto plus Equna die saubere Kombination ist.',
        href: '/blog/3-konten-modell-und-equna-warum-beides-zusammengehoert',
      },
      {
        title: 'Equna vs. Tricount',
        description: 'Tricount ist stark für Reisen — Equna ist auf laufende Familienkosten ausgerichtet.',
        href: '/blog/equna-vs-tricount-fuer-paare-und-familien',
      },
    ],
  },
  faq: {
    heading: 'Häufige Fragen',
    items: [
      {
        question: 'Wie viele Budget-Bereiche gibt es?',
        answer:
          'In der kostenlosen Version sind drei Budget-Bereiche enthalten — das reicht für die meisten Familien (z.B. Lebensmittel, Wohnen, Freizeit). In der Premium-Variante ist die Anzahl höher und Jahresbudgets möglich.',
      },
      {
        question: 'Können wir auch Ausgaben pro Kind erfassen?',
        answer:
          'Ja. Du kannst Kinder als Profile anlegen und Ausgaben direkt einem Kind zuordnen. Das ist auch für Co-Parents nützlich, die getrennt leben.',
      },
      {
        question: 'Bekommen wir eine Warnung, wenn ein Budget knapp wird?',
        answer:
          'Ja. Equna meldet, wenn ein Budget zu früh im Monat überzogen ist. Beide Eltern können entscheiden, ob umgeplant wird.',
      },
      {
        question: 'Wie sieht der Monatsabschluss aus?',
        answer:
          'Am Monatsende bestätigen beide Eltern, dass alles drin ist. Equna berechnet, wer wem wie viel überweist, und archiviert den Stand des Monats. Spätere Änderungen sind eingefroren.',
      },
      {
        question: 'Ist Equna kostenlos?',
        answer:
          'Ja. Die Grundversion ist kostenlos und deckt alle Familienfunktionen ab. Premium ist optional für längere Beleg-Speicherung und erweiterte Auswertungen.',
      },
    ],
  },
  finalCta: {
    heading: 'Familienbudget, das wirklich funktioniert',
    sub: 'Drei Minuten Setup, dann läuft das Erfassen für euch.',
  },
  uiStrings: {
    backToHome: '← Zur Startseite',
    related: 'Mehr lesen',
    legalPrivacy: 'Datenschutz',
    legalTerms: 'AGB',
    legalImprint: 'Impressum',
    legalSupport: 'Support',
    copyrightRights: 'Alle Rechte vorbehalten.',
    breadcrumbHome: 'Startseite',
    breadcrumbCurrent: 'Familienbudget-App',
  },
}

export default function FamilienbudgetSeoPage() {
  return <SeoLandingLayout content={content} />
}
